"use client"

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { logger } from "shared-ts/logger"

// A SidePanelId is the panel counterpart of a ModalId: a branded string that
// names one panel so it can be opened from anywhere. Panels are kept in their
// own context (separate from ModalContext) so a Modal can stack on top of an
// open SidePanel
export type SidePanelId = string & { readonly __sidePanelBrand: unique symbol }

export function defSidePanelId<const L extends string>(
  literal: L
): L & SidePanelId {
  return literal as L & SidePanelId
}

interface SidePanelState {
  active: SidePanelId | null
  // Use as React `key` for guaranteed remount on open
  mountKey: number
}

interface SidePanelCtx {
  open(id: SidePanelId): void
  close(): void
  isOpen(id: SidePanelId): boolean
  mountKey: number
  trackPanelComponent(id: SidePanelId): () => void
}

const sidePanelCtxLogger = logger.withScope("SidePanelCtx")

const SidePanelContext = createContext<SidePanelCtx | null>(null)

export function SidePanelContextProvider({
  children,
}: {
  children: ReactNode
}) {
  const [state, setState] = useState<SidePanelState>({
    active: null,
    mountKey: 0,
  })
  const panelComponentCounts = useRef<Map<SidePanelId, number>>(new Map())

  const trackPanelComponent = useCallback((id: SidePanelId) => {
    const map = panelComponentCounts.current

    const instances = (map.get(id) ?? 0) + 1
    map.set(id, instances)

    if (instances > 1) {
      sidePanelCtxLogger.error(
        `<SidePanel> components (${instances}) detected with duplicate ID: "${id}"`
      )
    }

    return () => {
      const remaining = (map.get(id) ?? 1) - 1
      remaining > 0 ? map.set(id, remaining) : map.delete(id)
    }
  }, [])

  const open = useCallback((id: SidePanelId) => {
    setState((prev) => {
      if (prev.active !== null && prev.active !== id) {
        sidePanelCtxLogger.warn(
          `Opening panel ${id} without explicitly closing ${prev.active}`
        )
      }
      return { active: id, mountKey: prev.mountKey + 1 }
    })
  }, [])

  const close = useCallback(() => {
    setState((prev) =>
      prev.active === null ? prev : { ...prev, active: null }
    )
  }, [])

  const isOpen = useCallback(
    (id: SidePanelId) => state.active === id,
    [state.active]
  )

  const value: SidePanelCtx = useMemo(
    () => ({
      open,
      close,
      isOpen,
      mountKey: state.mountKey,
      trackPanelComponent,
    }),
    [open, close, isOpen, state.mountKey, trackPanelComponent]
  )

  return (
    <SidePanelContext.Provider value={value}>
      {children}
    </SidePanelContext.Provider>
  )
}

/**
 * Binds side-panel control to a specific `SidePanelId`.
 * `mountKey` is reserved for "internal" use by the SidePanel component.
 */
export function useSidePanel(id: SidePanelId) {
  const ctx = useContext(SidePanelContext)
  if (!ctx)
    throw new Error("useSidePanel must be used within SidePanelContextProvider")

  const open = useCallback(() => ctx.open(id), [ctx, id])
  const isOpen = useMemo(() => ctx.isOpen(id), [ctx, id])

  return {
    open,
    close: ctx.close,
    isOpen,
    mountKey: ctx.mountKey,
  }
}

export function useSidePanelComponent(id: SidePanelId): void {
  const ctx = useContext(SidePanelContext)
  if (!ctx)
    throw new Error(
      "useSidePanelComponent must be used within SidePanelContextProvider"
    )

  useEffect(() => {
    return ctx.trackPanelComponent(id)
  }, [ctx, id])
}
